import { captureException } from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { query, queryClient } from 'config/api'
import { useEffect, useMemo } from 'react'
import { createGradeOverrideMap } from 'util/gradeOverrides'

export function useGradeOverrides(
  sectionId: string,
  assignmentId?: string,
  enabled = true
) {
  const result = useQuery({
    ...query.api.v1.sections.gradeOverrides.index.queryOptions({
      params: { sectionId },
      query: { assignmentId },
    }),
    enabled: enabled && Boolean(sectionId),
    staleTime: 30_000,
  })

  const responseError =
    result.error ??
    (result.data && 'error' in result.data ? result.data.error : null)

  useEffect(() => {
    if (!responseError) return

    captureException(responseError, {
      tags: { feature: 'grade-overrides' },
      extra: { sectionId, assignmentId },
    })
  }, [assignmentId, responseError, sectionId])

  const overrideMap = useMemo(() => {
    const overrides =
      result.data && !('error' in result.data) ? result.data : []

    return createGradeOverrideMap(overrides)
  }, [result.data])

  return {
    ...result,
    overrideMap,
  }
}

export function gradeOverridesQueryFilter() {
  return query.api.v1.sections.gradeOverrides.index.queryFilter()
}

export async function fetchGradeOverrideMap({
  sectionId,
  assignmentId,
}: {
  sectionId: string
  assignmentId?: string
}) {
  const response = await queryClient.fetchQuery({
    ...query.api.v1.sections.gradeOverrides.index.queryOptions({
      params: { sectionId },
      query: { assignmentId },
    }),
    staleTime: 30_000,
  })

  if ('error' in response) {
    captureException(response.error, {
      tags: { feature: 'grade-overrides' },
      extra: { sectionId, assignmentId },
    })
    throw response.error
  }

  return createGradeOverrideMap(response)
}
